import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '@/features/auth';

interface AuthGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * AuthGuard prevents authenticated users from accessing authentication pages
 * (like login, register, forgot password)
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  redirectTo = '/app/',
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // If user is authenticated, redirect them away from auth pages
  if (isAuthenticated) {
    // Check if there's a 'from' location to redirect to
    const from = location.state?.from?.pathname;
    const destination =
      from &&
      from !== '/login' &&
      from !== '/register' &&
      from !== '/forgot-password'
        ? from
        : redirectTo;

    return <Navigate to={destination} replace />;
  }

  // If user is not authenticated, allow access to auth pages
  return <>{children}</>;
};
